import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, Loader2, Phone } from "lucide-react";
import { MaskedLines, Reveal } from "../components/motion";
import { api } from "../lib/api";
import { INDUSTRIES, MODEL_VERSION, PC_OPTIONS, screenResult } from "../lib/planModel";

const STORE = "ohc_plan_check_v1";
const ATTR = "ohc_plan_check_attr";
const TOTAL = 9;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

const blank = {
  company: "", state: "", industry: "", fte: "", enrolled: "", coverage: "", cost: "",
  renewal: "", priorities: [], risk: "", health: "", timing: "",
};

const readAttr = (params) => {
  const saved = sessionStorage.getItem(ATTR);
  if (saved) return JSON.parse(saved);
  const get = (k) => params.get(k) || null;
  const utm = {
    utm_source: get("utm_source"), utm_medium: get("utm_medium"),
    utm_campaign: get("utm_campaign"), utm_content: get("utm_content"), utm_term: get("utm_term"),
  };
  const referrer = document.referrer || null;
  let host = "";
  try { host = referrer ? new URL(referrer).host : ""; } catch (e) { host = ""; }
  let source = "other";
  if (utm.utm_source === "qr") source = "qr";
  else if (utm.utm_source === "email" || utm.utm_medium === "email") source = "email";
  else if (utm.utm_source === "website" || (!utm.utm_source && host.endsWith("ohcbenefits.com"))) source = "website";
  else if (!utm.utm_source && !referrer) source = "direct";
  const attr = { ...utm, source, referrer, landing_path: window.location.pathname };
  sessionStorage.setItem(ATTR, JSON.stringify(attr));
  return attr;
};

const card = "w-full min-h-[48px] border border-border bg-cream px-5 py-3.5 text-left text-sm leading-snug text-ink/80 transition-all duration-200 hover:border-gold hover:bg-sand/50";
const cardOn = "w-full min-h-[48px] border border-forest bg-forest px-5 py-3.5 text-left text-sm leading-snug text-cream transition-all duration-200";
const field = "w-full min-h-[48px] border border-border bg-cream px-4 text-base text-ink placeholder:text-ink/35 focus:border-forest focus:outline-none";
const label = "block font-mono text-[10px] uppercase tracking-[0.25em] text-ink/50";
const primary = "inline-flex min-h-[48px] items-center justify-center gap-2 rounded-full bg-forest px-8 text-sm font-medium text-cream transition-all duration-300 hover:bg-moss disabled:cursor-not-allowed disabled:opacity-50";

const Choice = ({ options, value, onPick, name, multi = false }) => (
  <div className="mt-6 space-y-3">
    {options.map((o) => {
      const on = multi ? value.includes(o.value) : value === o.value;
      return (
        <button key={o.value} type="button" onClick={() => onPick(o.value)}
          data-testid={`pc-${name}-${o.value}`} className={on ? cardOn : card}>
          {o.label}
        </button>
      );
    })}
  </div>
);

const Err = ({ children }) => children
  ? <p className="mt-2 text-sm text-terra" data-testid="pc-error">{children}</p> : null;

export default function PlanCheck() {
  const [params] = useSearchParams();
  const [attr] = useState(() => readAttr(params));
  const [state, setState] = useState(() => {
    const saved = sessionStorage.getItem(STORE);
    return saved ? JSON.parse(saved) : { step: 0, answers: blank };
  });
  const [err, setErr] = useState("");
  const [contact, setContact] = useState({ name: "", email: "", phone: "", title: "", website_url: "" });
  const [consent, setConsent] = useState({ contact: false, marketing: false });
  const [sending, setSending] = useState(false);
  const [saved, setSaved] = useState(null);

  const { step, answers } = state;
  const set = (patch) => setState((s) => ({ ...s, answers: { ...s.answers, ...patch } }));
  const go = (n) => { setErr(""); setState((s) => ({ ...s, step: n })); };

  useEffect(() => { sessionStorage.setItem(STORE, JSON.stringify(state)); }, [state]);
  useEffect(() => {
    if (step >= 1 && step <= TOTAL) {
      window.posthog?.capture("plan_check_step", { step, source: attr.source });
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [step, attr.source]);

  const result = useMemo(() => (step >= 10 ? screenResult(answers) : null), [step, answers]);

  const advance = (next, picked = {}) => {
    const merged = { ...answers, ...picked };
    if (next === 6 && merged.coverage === "none" && !merged.cost) set({ cost: "unknown" });
    if (next === 7 && merged.coverage === "none" && !merged.renewal) set({ renewal: "unknown" });
    go(next);
  };

  const pickOne = (key, next) => (value) => { set({ [key]: value }); advance(next, { [key]: value }); };
  const toggle = (value) => set({
    priorities: answers.priorities.includes(value)
      ? answers.priorities.filter((p) => p !== value)
      : [...answers.priorities, value],
  });

  const numOk = (v) => { const n = Number(v); return Number.isFinite(n) && n >= 1 && n <= 5000; };
  const contactValid = contact.name.trim() && EMAIL_RE.test(contact.email.trim()) && consent.contact;

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    setErr("");
    try {
      const { data } = await api.post("/public/plan-check", {
        answers, contact: { name: contact.name.trim(), title: contact.title.trim(), email: contact.email.trim().toLowerCase(), phone: contact.phone.trim() },
        consent: { contact: consent.contact, marketing: consent.marketing },
        attribution: attr, result: screenResult(answers), model_version: MODEL_VERSION,
        website_url: contact.website_url,
      });
      setSaved(data);
      go(11);
      setTimeout(() => sessionStorage.removeItem(STORE), 30 * 60 * 1000);
    } catch (e2) {
      setErr("Something went wrong. Please try again, or call (713) 725-7413.");
    } finally {
      setSending(false);
    }
  };

  const restart = () => {
    sessionStorage.removeItem(STORE);
    setContact({ name: "", email: "", phone: "", title: "", website_url: "" });
    setConsent({ contact: false, marketing: false });
    setSaved(null);
    setState({ step: 0, answers: blank });
  };

  const Shell = ({ children, back }) => (
    <div className="mx-auto w-full max-w-2xl px-5 pb-24 pt-28 sm:px-6 lg:pt-36">
      {step >= 1 && step <= 10 ? (
        <>
          <div className="h-[3px] w-full bg-border" aria-hidden="true">
            <div className="h-full bg-gold transition-all duration-500"
                 style={{ width: `${(Math.min(step, TOTAL) / TOTAL) * 100}%` }} />
          </div>
          <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.25em] text-ink/45" data-testid="pc-progress">
            Step {Math.min(step, TOTAL)} of {TOTAL}
          </p>
        </>
      ) : null}
      {children}
      {back ? (
        <button type="button" onClick={() => go(back)} data-testid="pc-back"
          className="mt-10 inline-flex min-h-[48px] items-center gap-2 text-sm text-ink/55 hover:text-terra">
          <ArrowLeft className="h-4 w-4" /> Back
        </button>
      ) : null}
    </div>
  );

  const Q = ({ children, helper }) => (
    <>
      <h2 className="mt-7 font-serif text-3xl font-light leading-tight tracking-tight text-forest sm:text-4xl">
        {children}
      </h2>
      {helper ? <p className="mt-3 text-sm leading-relaxed text-ink/60">{helper}</p> : null}
    </>
  );

  if (step === 0) {
    return (
      <div data-testid="plan-check-page">
        <Shell>
          <h1 className="font-mono text-xs uppercase tracking-[0.3em] text-terra" data-testid="pc-kicker">
            Group Health Plan Check · For Texas Employers
          </h1>
          <MaskedLines as="p"
            className="mt-7 font-serif text-4xl font-light leading-[1.02] tracking-tighter text-forest sm:text-5xl"
            lines={["Which health plan setup", "fits your company?"]} />
          <Reveal delay={0.15}>
            <p className="mt-7 text-base leading-relaxed text-ink/70">
              Answer a few quick questions about your company and see which ways of funding health
              benefits are likely to fit, from traditional group plans to level-funded, PEO and ICHRA
              options. Takes about 2 minutes. I&rsquo;m Kyle Walker, a licensed Texas health insurance
              agent in The Woodlands, and I review every result personally.
            </p>
            <button type="button" onClick={() => go(1)} data-testid="pc-start" className={`${primary} mt-9`}>
              Start the Plan Check
            </button>
            <p className="mt-6 text-xs leading-relaxed text-ink/45">
              Preliminary screen for discussion only. Not a quote or an offer of coverage.
            </p>
          </Reveal>
        </Shell>
      </div>
    );
  }

  if (step === 11 && saved) {
    const company = answers.company;
    return (
      <div data-testid="plan-check-page">
        <Shell>
          <h1 className="font-mono text-xs uppercase tracking-[0.3em] text-terra">
            Group Health Plan Check · For Texas Employers
          </h1>
          <h2 className="mt-7 font-serif text-3xl font-light leading-tight tracking-tight text-forest sm:text-4xl">
            {company}, here are your likely-fit options
          </h2>
          <p className="mt-4 text-sm leading-relaxed text-ink/65">
            Based on your answers, these are the best starting points to price out. This is a
            preliminary screen, not a quote or a recommendation to buy.
          </p>
          <div className="mt-9 space-y-5">
            {result.top.map((o) => (
              <div key={o.id} data-testid={`pc-result-${o.id}`} className="border border-border bg-cream p-6">
                <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold">Option {o.rank}</span>
                <h3 className="mt-3 font-serif text-xl font-medium tracking-tight text-forest">{o.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-ink/70">{o.line}</p>
                {o.why ? <p className="mt-2 text-sm leading-relaxed text-ink/55">{o.why}</p> : null}
                <p className="mt-4 text-sm leading-relaxed text-ink/65">
                  <span className="mr-2 inline-block border border-gold/60 px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.2em] text-terra">
                    {o.elig === "Eligible" ? "Likely eligible" : "Possible, with conditions"}
                  </span>
                  {o.eligNote}
                </p>
                <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.18em] text-ink/40">
                  Employer risk: {o.riskLabel} · Admin effort: {o.adminLabel}
                </p>
              </div>
            ))}
          </div>
          {result.notes.length ? (
            <div className="mt-8 border-l-2 border-gold bg-sand/50 p-6" data-testid="pc-notes">
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-terra">Good to know</p>
              <ul className="mt-3 space-y-2">
                {result.notes.map((note) => (
                  <li key={note} className="text-sm leading-relaxed text-ink/70">{note}</li>
                ))}
              </ul>
            </div>
          ) : null}
          <div className="mt-8 border border-border bg-sand/40 p-6">
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-terra">What happens next</p>
            <p className="mt-3 text-sm leading-relaxed text-ink/70">
              I&rsquo;ll review your answers personally and reach out within one business day. In a
              30-minute consultation we&rsquo;ll look at your census and renewal and price the options
              that actually fit.
            </p>
          </div>
          <div className="mt-9 flex flex-col gap-4 sm:flex-row sm:items-center">
            <Link to="/contact?type=consultation" data-testid="pc-book" className={primary}>
              Book a consultation
            </Link>
            <a href="tel:+17137257413" data-testid="pc-call"
              className="inline-flex min-h-[48px] items-center gap-2 rounded-full border border-border px-7 text-sm text-forest hover:border-terra hover:text-terra">
              <Phone className="h-4 w-4" /> Call Kyle: (713) 725-7413
            </a>
          </div>
          <button type="button" onClick={restart} data-testid="pc-restart"
            className="mt-7 min-h-[48px] text-sm text-ink/50 underline underline-offset-4 hover:text-terra">
            Start over
          </button>
          <p className="mt-8 text-xs leading-relaxed text-ink/45">
            Preliminary screen for discussion only. Not a quote, an offer of coverage, or a guarantee
            of cost or eligibility. Final options depend on carrier underwriting, your census and plan
            details.
          </p>
        </Shell>
      </div>
    );
  }

  if (step === 10) {
    return (
      <div data-testid="plan-check-page">
        <Shell back={9}>
          <Q>Your results are ready.</Q>
          <p className="mt-3 text-sm leading-relaxed text-ink/65">
            I found {result.top.length} options that look like a fit for {answers.company}. Tell me
            who you are and I&rsquo;ll show them right here.
          </p>
          <form onSubmit={submit} className="mt-8 space-y-5" data-testid="pc-gate">
            <div>
              <label className={label} htmlFor="pc-name">Your name</label>
              <input id="pc-name" maxLength={100} required value={contact.name} className={`${field} mt-2`}
                data-testid="pc-name" onChange={(e) => setContact({ ...contact, name: e.target.value })} />
            </div>
            <div>
              <label className={label} htmlFor="pc-email">Work email</label>
              <input id="pc-email" type="email" required value={contact.email} className={`${field} mt-2`}
                data-testid="pc-email" onChange={(e) => setContact({ ...contact, email: e.target.value })} />
            </div>
            <div>
              <label className={label} htmlFor="pc-phone">Phone (optional)</label>
              <input id="pc-phone" type="tel" value={contact.phone} className={`${field} mt-2`}
                data-testid="pc-phone" onChange={(e) => setContact({ ...contact, phone: e.target.value })} />
            </div>
            <div>
              <label className={label} htmlFor="pc-title">Your role (optional)</label>
              <input id="pc-title" value={contact.title} className={`${field} mt-2`}
                data-testid="pc-title" onChange={(e) => setContact({ ...contact, title: e.target.value })} />
            </div>
            <input type="text" name="website_url" tabIndex={-1} autoComplete="off" aria-hidden="true"
              value={contact.website_url} onChange={(e) => setContact({ ...contact, website_url: e.target.value })}
              className="absolute left-[-9999px] h-px w-px opacity-0" />
            <label className="flex gap-3 text-sm leading-relaxed text-ink/70">
              <input type="checkbox" checked={consent.contact} data-testid="pc-consent-contact"
                onChange={(e) => setConsent({ ...consent, contact: e.target.checked })}
                className="mt-1 h-4 w-4 shrink-0 accent-[#1A3626]" />
              <span>I agree that Kyle Walker of OneHarvestCo. Benefits may contact me by email or
                phone about my results and my company&rsquo;s benefits options. No obligation, and I
                can opt out at any time.</span>
            </label>
            <label className="flex gap-3 text-sm leading-relaxed text-ink/70">
              <input type="checkbox" checked={consent.marketing} data-testid="pc-consent-marketing"
                onChange={(e) => setConsent({ ...consent, marketing: e.target.checked })}
                className="mt-1 h-4 w-4 shrink-0 accent-[#1A3626]" />
              <span>Also send me Benefits Decoded, occasional plain-English emails about benefits.
                Unsubscribe anytime.</span>
            </label>
            <Err>{err}</Err>
            <button type="submit" disabled={!contactValid || sending} data-testid="pc-submit" className={primary}>
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              See my results
            </button>
            <p className="text-xs leading-relaxed text-ink/45">
              I don&rsquo;t sell your information. Your answers are used only to prepare your results
              and follow up with you.
            </p>
          </form>
        </Shell>
      </div>
    );
  }

  const steps = {
    1: (
      <Shell back={0}>
        <Q>What&rsquo;s your company&rsquo;s name?</Q>
        <input maxLength={120} value={answers.company} className={`${field} mt-6`} data-testid="pc-company"
          onChange={(e) => set({ company: e.target.value })} />
        <Err>{err}</Err>
        <button type="button" data-testid="pc-continue" className={`${primary} mt-8`}
          onClick={() => (answers.company.trim() ? go(2) : setErr("Please enter your company name."))}>
          Continue
        </button>
      </Shell>
    ),
    2: (
      <Shell back={1}>
        <Q>Where are most of your employees based?</Q>
        <Choice name="state" value={answers.state} onPick={pickOne("state", 3)}
          options={[{ value: "TX", label: "Texas" }, { value: "OTHER", label: "Another state" }]} />
      </Shell>
    ),
    3: (
      <Shell back={2}>
        <Q>What industry are you in?</Q>
        <Choice name="industry" value={answers.industry} onPick={pickOne("industry", 4)} options={INDUSTRIES} />
      </Shell>
    ),
    4: (
      <Shell back={3}>
        <Q>How many people work for you?</Q>
        <div className="mt-6 space-y-6">
          <div>
            <label className={label} htmlFor="pc-fte">
              Full-time employees (30+ hours a week), counting part-timers as fractions if you know them
            </label>
            <input id="pc-fte" inputMode="numeric" value={answers.fte} className={`${field} mt-2`}
              data-testid="pc-fte" onChange={(e) => set({ fte: e.target.value })} />
          </div>
          <div>
            <label className={label} htmlFor="pc-enrolled">
              About how many are on, or would join, the health plan? (optional)
            </label>
            <input id="pc-enrolled" inputMode="numeric" value={answers.enrolled} className={`${field} mt-2`}
              data-testid="pc-enrolled" onChange={(e) => set({ enrolled: e.target.value })} />
          </div>
        </div>
        <Err>{err}</Err>
        <button type="button" data-testid="pc-continue" className={`${primary} mt-8`}
          onClick={() => {
            if (!numOk(answers.fte) || (answers.enrolled && !numOk(answers.enrolled))) {
              setErr("Please enter a number between 1 and 5,000.");
              return;
            }
            advance(5);
          }}>
          Continue
        </button>
      </Shell>
    ),
    5: (
      <Shell back={4}>
        <Q>Do you offer health coverage today?</Q>
        <Choice name="coverage" value={answers.coverage} onPick={pickOne("coverage", 6)} options={PC_OPTIONS.coverage} />
      </Shell>
    ),
    6: (
      <Shell back={5}>
        <Q helper={answers.coverage === "none" ? 'No plan today? Choose "Not sure / no plan today."' : null}>
          Roughly what does health coverage cost per enrolled employee per month today (total premium,
          employer + employee)?
        </Q>
        <Choice name="cost" value={answers.cost} onPick={pickOne("cost", 7)} options={PC_OPTIONS.cost} />
      </Shell>
    ),
    7: (
      <Shell back={6}>
        <Q>What increase did (or do) you expect at your next renewal?</Q>
        <Choice name="renewal" value={answers.renewal} onPick={pickOne("renewal", 8)} options={PC_OPTIONS.renewal} />
      </Shell>
    ),
    8: (
      <Shell back={7}>
        <Q>What matters most? Pick all that apply.</Q>
        <Choice name="priorities" multi value={answers.priorities} onPick={toggle} options={PC_OPTIONS.priorities} />
        <Err>{err}</Err>
        <button type="button" data-testid="pc-continue" className={`${primary} mt-8`}
          onClick={() => (answers.priorities.length ? go(9) : setErr("Pick at least one."))}>
          Continue
        </button>
      </Shell>
    ),
    9: (
      <Shell back={8}>
        <Q>Three quick ones about risk, health and timing</Q>
        <div className="mt-6 space-y-9">
          <div>
            <p className={label}>How do you feel about cost swings?</p>
            <Choice name="risk" value={answers.risk} onPick={(v) => set({ risk: v })} options={PC_OPTIONS.risk} />
          </div>
          <div>
            <p className={label}>How would you describe your team&rsquo;s health claims?</p>
            <Choice name="health" value={answers.health} onPick={(v) => set({ health: v })} options={PC_OPTIONS.health} />
          </div>
          <div>
            <p className={label}>When is your next renewal or start date?</p>
            <Choice name="timing" value={answers.timing} onPick={(v) => set({ timing: v })} options={PC_OPTIONS.timing} />
          </div>
        </div>
        <Err>{err}</Err>
        <button type="button" data-testid="pc-continue" className={`${primary} mt-8`}
          onClick={() => ((answers.risk && answers.health && answers.timing) ? go(10) : setErr("Please pick one."))}>
          Continue
        </button>
      </Shell>
    ),
  };

  return <div data-testid="plan-check-page">{steps[step] ?? steps[1]}</div>;
}
